import { GetMetricDataCommand, MetricDataQuery } from '@aws-sdk/client-cloudwatch';
import { getCloudWatchClient } from 'src/services/aws/CloudWatchMetricsService';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { logger } from 'src/logger';

/**
 * Shared CloudWatch dashboard metric extraction.
 *
 * Generalized out of `oe-report/hooks/useKpiMetricsData.ts` so any consumer can pull
 * the numeric values behind a CloudWatch dashboard's `singleValue` widgets without
 * re-implementing the GetMetricData plumbing. `useKpiMetricsData` keeps its
 * domain-specific row shaping (API / Database / User Activity) and now sources the raw
 * per-widget numbers from here; the KPI Metrics page uses `extractSingleValueWidgets`
 * to feed a dashboard-agnostic AI narrative.
 *
 * Only `singleValue` widgets are read — they carry the high-signal aggregate numbers
 * (totals, averages, P90s) that summarize a dashboard. Time-series graph widgets are
 * intentionally out of scope.
 */

const DEFAULT_REGION = ENVIRONMENT_VARIABLES.env.Region || 'us-west-2';

/** One resolved metric line from a `singleValue` widget. */
export interface SingleValueResult {
  /** The CloudWatch result label (e.g. "FinSuite Availability"). */
  label: string;
  /** Sum of all data points in the range — the right aggregate for count-like metrics. */
  sum: number;
  /** Mean of all data points — the right aggregate for rate/percent/latency metrics. */
  avg: number;
  /** Number of data points that contributed, for callers that need to weight results. */
  count: number;
}

/** A `singleValue` widget's title paired with its resolved metric lines. */
export interface SingleValueWidgetGroup {
  /** The widget's configured title (e.g. "API Availability"). */
  title: string;
  /** Resolved metric lines for the widget. */
  entries: SingleValueResult[];
}

/**
 * Computes the full time-range period for singleValue widget queries.
 * Must be a valid CloudWatch period:
 * - Divisible by 60 for periods ≤ 86400
 * - Divisible by 86400 for periods > 86400
 * This replicates `setPeriodToTimeRange: true` behavior in CloudWatch dashboards.
 */
export const computeWidgetPeriod = (startTime: Date, endTime: Date): number => {
  const rangeSeconds = Math.ceil((endTime.getTime() - startTime.getTime()) / 1000);
  if (rangeSeconds <= 86400) {
    // ≤ 1 day: round up to nearest 60s
    return Math.ceil(rangeSeconds / 60) * 60;
  }
  // > 1 day: round up to nearest multiple of 86400 (CW requirement for large periods)
  return Math.ceil(rangeSeconds / 86400) * 86400;
};

/**
 * Queries a single singleValue widget by sending ALL its metrics + expressions in one GetMetricData call.
 * Within a single widget, all expression references are self-contained.
 * Returns only the VISIBLE results (the top-level per-app values).
 *
 * Respects the widget's `setPeriodToTimeRange` property: when true (default for
 * singleValue widgets), overrides all metric/expression periods to match the full
 * query time range, replicating native CloudWatch dashboard behavior.
 */
export const querySingleValueWidget = async (
  rawMetrics: unknown[][],
  startTime: Date,
  endTime: Date,
  setPeriodToTimeRange = true
): Promise<SingleValueResult[]> => {
  const queries: MetricDataQuery[] = [];
  const queryRegions: string[] = []; // Parallel array tracking each query's target region
  let idCounter = 0;
  const effectivePeriod = setPeriodToTimeRange ? computeWidgetPeriod(startTime, endTime) : undefined;

  for (const metricArr of rawMetrics) {
    if (!Array.isArray(metricArr) || metricArr.length === 0) continue;

    // Expression entry: [{expression, label, id, visible, ...}]
    if (metricArr.length === 1 && typeof metricArr[0] === 'object' && metricArr[0] !== null && 'expression' in metricArr[0]) {
      const expr = metricArr[0] as Record<string, unknown>;
      const id = (expr.id as string) || `e_${idCounter++}`;
      queries.push({
        Id: id,
        Expression: expr.expression as string,
        Label: (expr.label as string) || '',
        Period: effectivePeriod || (expr.period as number) || 3600,
        ReturnData: expr.visible !== false
      });
      queryRegions.push(DEFAULT_REGION);
      continue;
    }

    // Standard metric: [Namespace, MetricName, DimKey1, DimValue1, ..., {options}]
    const lastItem = metricArr[metricArr.length - 1];
    const hasOptions = typeof lastItem === 'object' && lastItem !== null && !Array.isArray(lastItem);
    const options = hasOptions ? (lastItem as Record<string, unknown>) : {};

    if (typeof metricArr[0] !== 'string') continue;

    const namespace = metricArr[0] as string;
    const metricName = metricArr[1] as string;

    const dimEntries = hasOptions ? metricArr.slice(2, -1) : metricArr.slice(2);
    const dimensions: { Name: string; Value: string }[] = [];
    for (let i = 0; i < dimEntries.length; i += 2) {
      if (typeof dimEntries[i] === 'string' && typeof dimEntries[i + 1] === 'string') {
        dimensions.push({ Name: dimEntries[i] as string, Value: dimEntries[i + 1] as string });
      }
    }

    const id = (options.id as string) || `m_${idCounter++}`;
    const stat = (options.stat as string) || 'Average';
    const period = effectivePeriod || (options.period as number) || 3600;
    const visible = options.visible !== false;
    const accountId = options.accountId as string | undefined;
    const region = (options.region as string) || DEFAULT_REGION;

    queries.push({
      Id: id,
      MetricStat: {
        Metric: {
          Namespace: namespace,
          MetricName: metricName,
          Dimensions: dimensions
        },
        Period: period,
        Stat: stat
      },
      Label: (options.label as string) || metricName,
      ReturnData: visible,
      AccountId: accountId
    });
    queryRegions.push(region);
  }

  if (queries.length === 0) return [];

  // Build a map of metric ID → region for resolving expression dependencies.
  const idToRegion = new Map<string, string>();
  for (let i = 0; i < queries.length; i++) {
    const q = queries[i];
    if (q.Id && q.MetricStat) idToRegion.set(q.Id, queryRegions[i]);
  }

  // Referenced identifiers in an expression (metric ids, other expression ids, and
  // function names like FILL/IF/SEARCH — the latter simply won't resolve to a region).
  const referencedIdsOf = (expression: string): string[] => expression.match(/\b[a-z][a-z0-9_]*\b/gi) || [];

  // Map every *named* expression id to the ids it references, so an expression that
  // references only other expressions (e.g. a multi-API app whose value is
  // `FILL(m0,0)+FILL(m1,0)`) can still be traced to a concrete metric's region.
  const exprRefs = new Map<string, string[]>();
  for (const q of queries) {
    if (q.Expression && q.Id) exprRefs.set(q.Id, referencedIdsOf(q.Expression));
  }

  // Resolve an id to its region: metric ids resolve directly; expression ids resolve to
  // the region of the first metric they *transitively* reference. CloudWatch metric math
  // cannot span regions, so any reachable metric yields the correct region. Returns
  // undefined only when nothing resolvable is reached (e.g. a bare function name).
  const resolveRegion = (id: string, seen: Set<string>): string | undefined => {
    const direct = idToRegion.get(id);
    if (direct) return direct;
    if (seen.has(id)) return undefined; // cycle guard
    seen.add(id);
    const refs = exprRefs.get(id);
    if (!refs) return undefined;
    for (const ref of refs) {
      const region = resolveRegion(ref, seen);
      if (region) return region;
    }
    return undefined;
  };

  // Region for an expression query (which may itself be unnamed, e.g. the visible
  // top-level line): the region of the first referenced id that resolves transitively.
  const regionForExpression = (expression: string): string => {
    for (const ref of referencedIdsOf(expression)) {
      const region = resolveRegion(ref, new Set<string>());
      if (region) return region;
    }
    return DEFAULT_REGION;
  };

  // Group queries by region, keeping expressions with their (transitively) referenced
  // metrics so no GetMetricData call references an id that landed in another region's call.
  const queryGroups = new Map<string, MetricDataQuery[]>();
  for (let i = 0; i < queries.length; i++) {
    const q = queries[i];
    const targetRegion = q.Expression ? regionForExpression(q.Expression) : queryRegions[i];
    if (!queryGroups.has(targetRegion)) queryGroups.set(targetRegion, []);
    queryGroups.get(targetRegion)!.push(q);
  }

  try {
    const results: { key: string; label: string; sum: number; avg: number; count: number }[] = [];

    for (const [region, regionQueries] of queryGroups) {
      // Paginate GetMetricData — results may span multiple pages
      let nextToken: string | undefined;
      do {
        const response = await getCloudWatchClient(region).send(
          new GetMetricDataCommand({ StartTime: startTime, EndTime: endTime, MetricDataQueries: regionQueries, NextToken: nextToken })
        );

        for (const r of response.MetricDataResults || []) {
          if (!r.Label || !r.Values || r.Values.length === 0) continue;
          const validValues = r.Values.filter((v) => isFinite(v) && !isNaN(v));
          if (validValues.length === 0) continue;
          const sum = validValues.reduce((s, v) => s + v, 0);
          // Merge by region+label to avoid cross-region collisions for same label
          const mergeKey = `${region}::${r.Label}`;
          const existing = results.find((e) => e.key === mergeKey);
          if (existing) {
            existing.sum += sum;
            existing.count += validValues.length;
            existing.avg = existing.sum / existing.count;
          } else {
            results.push({ key: mergeKey, label: r.Label, sum, avg: sum / validValues.length, count: validValues.length });
          }
        }
        nextToken = response.NextToken;
      } while (nextToken);
    }

    return results;
  } catch (err) {
    logger.error('[querySingleValueWidget] Failed', err instanceof Error ? err : new Error(String(err)));
    return [];
  }
};

/**
 * Extracts every `singleValue` widget from a CloudWatch dashboard definition body and
 * resolves its metric values over the given time range. Dashboard-agnostic: it makes no
 * assumptions about metric names or app labels, so it works for any dashboard.
 *
 * Widgets that resolve to no data are omitted, so an empty result means the dashboard
 * has no readable singleValue metrics for the range.
 */
export const extractSingleValueWidgets = async (body: Record<string, unknown>, startTime: Date, endTime: Date): Promise<SingleValueWidgetGroup[]> => {
  const rawWidgets = (body.widgets || []) as Record<string, unknown>[];
  const svWidgets = rawWidgets.filter((rw) => rw.type === 'metric' && (rw.properties as Record<string, unknown>)?.view === 'singleValue');

  const groups: SingleValueWidgetGroup[] = [];
  for (const svWidget of svWidgets) {
    const props = svWidget.properties as Record<string, unknown>;
    const title = (props.title as string) || '';
    const metrics = props.metrics as unknown[][] | undefined;
    if (!metrics) continue;

    const usePeriodOverride = props.setPeriodToTimeRange !== false;
    const entries = await querySingleValueWidget(metrics, startTime, endTime, usePeriodOverride);
    if (entries.length > 0) groups.push({ title, entries });
  }
  return groups;
};
